import type {
  ConditionFieldRow,
  ConditionFillFinding,
  StatementCondition,
} from "../../../../../api/types"
import {
  conditionFieldReader,
  verdictBasisFilled,
} from "../../studio/conditionRows"
import {
  conditionSpec,
  fieldsRequiredFor,
  type ConditionSpec,
} from "../../studio/conditions"
import { findingHealed } from "../../studio/flowState"
import { memberOf } from "../../studio/memberSets"
import type { Verdict } from "../../studio/studioTypes"
import { normalizeCas } from "../mapping/conditionRowMerge"

/**
 * The substance table's pure derivations (review-mask prototype, round 5):
 * what `SubstanceEditor` renders is ONE ROW PER SUBSTANCE of the statement,
 * its articles behind a chevron, never one field group per article. Every
 * function here is a plain function over the card's disclosures, the
 * persisted condition rows and the statement's reviewer bindings, so the
 * table's reading of the data is testable without a render.
 *
 * THE DATA MODEL DOES NOT CHANGE. Persistence stays the per-article
 * `ConditionFieldRow` (`{articleNumber, substanceCas, framework, fieldKey}`)
 * plus the reviewer binding (`__connected__`, `reviewerBindings.ts`), the
 * exact contract the approve path writes `mc_article_substances` from. The
 * table only READS those rows substance-first: a value that is the same on
 * every article of a substance is shown once (and a value typed there fans
 * out to every article, `useConditionFieldWrites`'s `commitMany`), values
 * that differ per article show as "n Werte" and open the per-article rows.
 */

/** The one CAS-less slot's key: every disclosure the extraction could not
 *  name a substance for shares the SAME persisted address (`substanceCas:
 *  null`), so they fold into ONE row, exactly like the per-article grid's
 *  `substanceGroups` (its own doc says why). */
export const NO_CAS = " nocas"

export interface SubstanceEntry {
  /** Normalised CAS (the persisted address) or `NO_CAS`. */
  key: string
  /** The persisted `substanceCas` address, null for the nameless slot. */
  cas: string | null
  /** Deduped substance names across the folded disclosures, first-seen
   *  order. Empty for a reviewer-added substance (the registry names it). */
  names: string[]
  /** Deduped, non-empty `location` strings across the disclosures. */
  locations: string[]
  /** Deduped, non-empty EXTRACTED concentrations: a hint while the
   *  persisted value is empty, never a value of its own. */
  concentrations: string[]
  /** Deduped, non-empty extracted values per SPEC FIELD key. */
  fieldValues: Record<string, string[]>
  /** Display-only: what the reader refused to accept, per field. */
  rejectedValues: Record<string, string[]>
  /** Every disclosure of this substance the extraction bound to specific
   *  articles, none of them article-agnostic: the substance then applies to
   *  `serverArticles` only, not to the whole resolved scope. */
  serverBound: boolean
  /** The union of the disclosures' own article bindings. */
  serverArticles: string[]
  /** No disclosure names this substance: it exists on the statement only
   *  through this statement's own reviewer binding, i.e. the reviewer added
   *  it by hand. Removable (dropping the binding drops the row); an
   *  extracted substance is not (the record has no tombstone for it). */
  added: boolean
  /** D6 positions of this substance's disclosures M3 could not connect to the
   *  register: the addresses create-and-link names. */
  unresolved: number[]
}

function blank(
  key: string,
  cas: string | null,
  added: boolean,
): SubstanceEntry {
  return {
    key,
    cas,
    names: [],
    locations: [],
    concentrations: [],
    fieldValues: {},
    rejectedValues: {},
    serverBound: false,
    serverArticles: [],
    added,
    unresolved: [],
  }
}

/**
 * One entry per DISTINCT substance of the statement: the disclosures folded
 * by normalised CAS (name plays no part, see `NO_CAS`), then every substance
 * this statement's reviewer bindings name that no disclosure does (added by
 * hand). Order: disclosure order, then bindings.
 *
 * `namelessWhenEmpty`: a substance-independent regime (halogen-free, no
 * `spec.concentration`) captures its fields on the nameless slot, so a
 * statement with no disclosure at all still gets that one row; a CAS-based
 * regime with nothing extracted gets no row at all, the "+ Stoff" search is
 * the way in.
 */
export function substanceEntries(
  conditions: readonly StatementCondition[],
  bindings: ReadonlyMap<string, readonly string[]>,
  namelessWhenEmpty = false,
): SubstanceEntry[] {
  const order: string[] = []
  const byKey = new Map<string, SubstanceEntry>()
  // Per key, the `serverArticles` already listed: a 10k-article binding
  // dedups in one pass instead of one `includes` scan per article.
  const listed = new Map<string, Set<string>>()
  // Per key: did ANY disclosure apply to the whole scope (empty binding)?
  const agnostic = new Set<string>()
  for (const c of conditions) {
    const cas = normalizeCas(c.cas)
    const key = cas ?? NO_CAS
    let entry = byKey.get(key)
    if (!entry) {
      entry = blank(key, cas, false)
      byKey.set(key, entry)
      order.push(key)
    }
    const bound = c.articleNumbers ?? []
    if (bound.length === 0) agnostic.add(key)
    let seenArticles = listed.get(key)
    if (!seenArticles) {
      seenArticles = new Set()
      listed.set(key, seenArticles)
    }
    for (const a of bound) {
      if (seenArticles.has(a)) continue
      seenArticles.add(a)
      entry.serverArticles.push(a)
    }
    if (c.unresolved && c.disclosureIndex != null) {
      entry.unresolved.push(c.disclosureIndex)
    }
    const name = (c.substance ?? "").trim()
    if (name && !entry.names.includes(name)) entry.names.push(name)
    if (c.location && !entry.locations.includes(c.location)) {
      entry.locations.push(c.location)
    }
    if (c.concentration && !entry.concentrations.includes(c.concentration)) {
      entry.concentrations.push(c.concentration)
    }
    for (const { key: fieldKey, value } of c.fieldValues ?? []) {
      if (!value) continue
      const seen = (entry.fieldValues[fieldKey] ??= [])
      if (!seen.includes(value)) seen.push(value)
    }
    for (const { field, value } of c.rejectedValues ?? []) {
      if (!value) continue
      const seen = (entry.rejectedValues[field] ??= [])
      if (!seen.includes(value)) seen.push(value)
    }
  }
  for (const key of order) {
    const entry = byKey.get(key)!
    entry.serverBound = !agnostic.has(key) && entry.serverArticles.length > 0
  }
  if (order.length === 0 && namelessWhenEmpty) {
    const entry = blank(NO_CAS, null, false)
    byKey.set(NO_CAS, entry)
    order.push(NO_CAS)
  }
  for (const [cas, articles] of bindings) {
    if (articles.length === 0 || byKey.has(cas)) continue
    byKey.set(cas, blank(cas, cas, true))
    order.push(cas)
  }
  return order.map((key) => byKey.get(key)!)
}

/**
 * The articles ONE substance applies to, in the resolved scope's own order:
 * this statement's reviewer binding when it has one (the reviewer read the
 * document and the extraction did not, TUR-1211), else the extraction's own
 * binding when every disclosure carried one, else the whole resolved scope.
 * The same rule the removed `conditionsForArticle` applied
 * per disclosure, read per substance.
 */
export function entryArticles(
  entry: SubstanceEntry,
  resolvedArticles: readonly string[],
  bindings: ReadonlyMap<string, readonly string[]>,
): string[] {
  const reviewerBound = entry.cas ? bindings.get(entry.cas) : undefined
  if (reviewerBound && reviewerBound.length > 0) {
    return resolvedArticles.filter((a) => memberOf(reviewerBound, a))
  }
  if (entry.serverBound) {
    return resolvedArticles.filter((a) => memberOf(entry.serverArticles, a))
  }
  return [...resolvedArticles]
}

export interface FieldAddress {
  substanceCas: string | null
  framework: string | null
  fieldKey: string
}

/** Every field address (minus the article axis) the table renders for a
 *  substance under this spec: the concentration cell (`framework: null`,
 *  shared across Verordnungen) plus each framework-specific field. */
/**
 * The extracted concentration as the number the product can actually store.
 *
 * THE BUG THIS FIXES. The one-click accept commits the extracted string verbatim, so a
 * document reading "0.5 %" put `"0.5 %"` — percent sign and all — into a field the backend
 * parses with `float()`. That value is not a number for the master-data write (it is kept as
 * free text) and it is not a number for Turnus Memory either, so correcting an extracted
 * concentration — the commonest gesture on this screen — silently produced a cell the rest of
 * the app could not use. Measured on sandbox 17.09: `"0.05 %"` saved on a genuine correction,
 * and the org had captured nothing at all, ever.
 *
 * ONLY THE PERCENT SIGN, and only when what is left is a number. This field's unit IS
 * per-cent, so the sign is redundant rather than informative and dropping it cannot change a
 * value. Every other unit is left exactly as it was: "35 ppm" means 0.0035 %, and turning it
 * into `35` would be wrong by four orders of magnitude in the lenient direction, on a
 * compliance threshold. "< 0.1", "bis zu 4 %" and "0,28" are likewise returned untouched —
 * they are not numbers, and this function's job is to remove noise, never to interpret.
 *
 * LIVES HERE, not on the editor, because this module is the pure half and the editor is not.
 * It was on `ConditionFieldsEditor` until that file was split; a rename carried the call site
 * away from the helper and the fix was silently lost in a merge that git called clean.
 */
export function concentrationSuggestionValue(raw: string): string {
  const stripped = raw.trim().replace(/\s*%$/, "").trim()
  if (!isBareNumber(stripped)) return raw
  return stripped
}

/** A plain decimal figure with no unit or qualifier: "1.2" yes; "35 ppm", "< 0.1" and
 *  "0,28" no. Decimal syntax only: `Number()` also reads "0x10" or "0b10", which the
 *  backend's `float()` rejects and keeps as free text. */
const DECIMAL = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/
function isBareNumber(value: string): boolean {
  return DECIMAL.test(value) && Number.isFinite(Number(value))
}

/**
 * A persisted concentration as the reviewer reads it. A bare number IS a percentage
 * (the backend's own rule, `_numeric_concentration`), so it is shown with its unit: a
 * bare "1.2" left the reviewer unable to tell % w/w from ppm (prod test 2026-10-02,
 * F4). A value that names its own unit, or says something other than a figure, is
 * shown exactly as written.
 */
export function concentrationDisplay(value: string): string {
  const trimmed = value.trim()
  return isBareNumber(trimmed) ? `${trimmed} %` : value
}

export function fieldAddressesFor(
  spec: ConditionSpec,
  framework: string,
  cas: string | null,
): FieldAddress[] {
  const out: FieldAddress[] = []
  if (spec.concentration) {
    out.push({ substanceCas: cas, framework: null, fieldKey: "concentration" })
  }
  for (const f of spec.fields) {
    out.push({ substanceCas: cas, framework, fieldKey: f.key })
  }
  return out
}

export interface CellState {
  /** The one value every article carries (`""` when uniformly empty), or
   *  `null` when the articles' persisted values differ. */
  shared: string | null
  /** The persisted value per article, `""` where none is on record. */
  values: Map<string, string>
  /** The distinct persisted values, first-seen order, `""` included. */
  distinct: string[]
}

/** One field of one substance across its articles, read off the persisted
 *  rows. `shared` is the table's collapsed cell; `values` its per-article
 *  rows. */
export function cellState(
  rows: readonly ConditionFieldRow[],
  articles: readonly string[],
  address: FieldAddress,
): CellState {
  const values = new Map<string, string>()
  const distinct: string[] = []
  const seen = new Set<string>()
  const valueOf = conditionFieldReader(rows, address)
  for (const articleNumber of articles) {
    const value = valueOf(articleNumber)
    values.set(articleNumber, value)
    if (!seen.has(value)) {
      seen.add(value)
      distinct.push(value)
    }
  }
  return {
    shared: distinct.length <= 1 ? (distinct[0] ?? "") : null,
    values,
    distinct,
  }
}

/** The findings per article, built once per findings array (its identity, the
 *  read model's own list; `memberSets.ts`'s rule: never changed in place, the
 *  length check only catches growth), so the open counts over a 10k-article
 *  substance ask each article once instead of scanning every finding per empty
 *  article. Row order kept. */
const FINDINGS_BY_ARTICLE = new WeakMap<
  readonly ConditionFillFinding[],
  { length: number; byArticle: Map<string, ConditionFillFinding[]> }
>()

function findingsOf(
  findings: readonly ConditionFillFinding[],
  articleNumber: string,
): readonly ConditionFillFinding[] {
  let cached = FINDINGS_BY_ARTICLE.get(findings)
  if (!cached || cached.length !== findings.length) {
    const byArticle = new Map<string, ConditionFillFinding[]>()
    for (const f of findings) {
      const list = byArticle.get(f.article)
      if (list) list.push(f)
      else byArticle.set(f.article, [f])
    }
    cached = { length: findings.length, byArticle }
    FINDINGS_BY_ARTICLE.set(findings, cached)
  }
  return cached.byArticle.get(articleNumber) ?? []
}

/** True when a backend `data_gap` finding is OPEN for this exact
 *  (article, CAS): the predicate that sorts an empty concentration into the
 *  calm data-gap note rather than the amber doubt chip (2026-09-04 product
 *  decision). */
export function openDataGap(
  findings: readonly ConditionFillFinding[],
  rows: readonly ConditionFieldRow[],
  articleNumber: string,
  addressCas: string | null,
): boolean {
  return findingsOf(findings, articleNumber).some(
    (f) =>
      (normalizeCas(f.substance) ?? null) === addressCas &&
      !findingHealed(f, rows),
  )
}

/** Whether an EMPTY concentration on this (article, CAS) is still a
 *  required field: not once a `data_gap` finding is open for it, not once
 *  the verdict basis is filled, never on a non_compliant statement
 *  (`fieldsRequiredFor`). The one predicate both the cell's amber border
 *  and the open count read, so they cannot drift apart. */
export function concentrationRequired(
  spec: ConditionSpec,
  verdict: Verdict | null,
  rows: readonly ConditionFieldRow[],
  findings: readonly ConditionFillFinding[],
  framework: string,
  articleNumber: string,
  addressCas: string | null,
): boolean {
  return (
    fieldsRequiredFor(verdict) &&
    !!spec.concentration?.required &&
    !openDataGap(findings, rows, articleNumber, addressCas) &&
    !verdictBasisFilled(spec, rows, framework, articleNumber, addressCas)
  )
}

export interface OpenCounts {
  /** Open required fields that still cast doubt on the verdict (amber). */
  doubt: number
  /** Empty concentrations whose backend finding is a `data_gap`: the calm
   *  note, never blocking. */
  dataGap: number
}

/**
 * The open-count status line, counted AT THE TABLE'S OWN GRANULARITY: a
 * field shown once (uniform across the substance's articles) counts once
 * when empty, never once per article ("20 Pflichtfelder offen" on a blanket
 * letter was the defect the collapse fixed); a field whose values differ
 * counts each empty article, which is exactly the per-article rows the table
 * opens for it. Same doubt/data-gap split as the removed grid's
 * `openFieldCounts`: a published `data_gap` finding counts `dataGap`
 * regardless of the verdict basis; only when absent does the verdict basis
 * decide between `doubt` and nothing. Display only: the approve gate reads
 * `flowState`, never this.
 *
 * Deliberately NOT the same count as `openIssuesByCard` in
 * `footer/nextOpenPoint.ts`: that one drives navigation and the approve
 * gate from `flowState`, one entry per blocking issue of a card; this one
 * is the substance table's own display badge over condition FIELDS. They
 * answer different questions and will disagree, so do not merge them.
 */
export function openCounts(
  entries: readonly SubstanceEntry[],
  articlesOf: (entry: SubstanceEntry) => readonly string[],
  card: { framework: string; verdict: Verdict | null },
  rows: readonly ConditionFieldRow[],
  findings: readonly ConditionFillFinding[],
): OpenCounts {
  const spec = conditionSpec(card.framework)
  let doubt = 0
  let dataGap = 0
  const countConcentration = (articleNumber: string, cas: string | null) => {
    if (openDataGap(findings, rows, articleNumber, cas)) dataGap += 1
    else if (
      !verdictBasisFilled(spec, rows, card.framework, articleNumber, cas)
    )
      doubt += 1
  }
  for (const entry of entries) {
    const articles = articlesOf(entry)
    if (articles.length === 0) continue
    for (const address of fieldAddressesFor(spec, card.framework, entry.cas)) {
      const isConcentration = address.framework === null
      const required = isConcentration
        ? !!spec.concentration?.required
        : (spec.fields.find((f) => f.key === address.fieldKey)?.required ??
          false)
      if (!required) continue
      const cell = cellState(rows, articles, address)
      const empties =
        cell.shared === null
          ? articles.filter((a) => cell.values.get(a) === "")
          : cell.shared === ""
            ? [articles[0]]
            : []
      for (const articleNumber of empties) {
        if (isConcentration) countConcentration(articleNumber, entry.cas)
        else doubt += 1
      }
    }
  }
  return { doubt: fieldsRequiredFor(card.verdict) ? doubt : 0, dataGap }
}
